import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Modal Component
 * Provides a reusable modal UI component for displaying, hiding, and managing modal content.
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Confirm Action"
 * >
 *   <p>This is the modal content.</p>
 *   <button onClick={handleConfirm}>Confirm</button>
 * </Modal>
 * ```
 */
import { useEffect } from 'react';
/**
 * Modal Component
 * Handles the display, hiding, and basic interactions of a modal.
 */
export const Modal = ({ isOpen, onClose, title, children, width = 'max-w-md', className = '', closeOnBackdropClick = true, closeOnEsc = true, }) => {
    // Handle ESC key to close
    useEffect(() => {
        const handleEscKey = (event) => {
            if (isOpen && closeOnEsc && event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            // Add keyboard event listener
            document.addEventListener('keydown', handleEscKey);
            // Prevent background scrolling and compensate for scrollbar width
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            const originalPaddingRight = document.body.style.paddingRight;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            // Cleanup function
            return () => {
                document.removeEventListener('keydown', handleEscKey);
                document.body.style.overflow = '';
                document.body.style.paddingRight = originalPaddingRight;
            };
        }
        // Cleanup for when modal is not open initially or closes
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, closeOnEsc, onClose]);
    // Handle backdrop click
    const handleBackdropClick = (e) => {
        // Ensure the click is on the backdrop and not the modal content
        if (closeOnBackdropClick && e.target === e.currentTarget) {
            onClose();
        }
    };
    // If the modal is not open, render nothing
    if (!isOpen)
        return null;
    return (_jsx("div", { className: "fixed inset-0 z-[1050] overflow-y-auto", "aria-labelledby": "modal-title", role: "dialog", "aria-modal": "true", children: _jsxs("div", { className: "flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0", onClick: handleBackdropClick, children: [_jsx("div", { className: "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity", "aria-hidden": "true" }), _jsx("span", { className: "hidden sm:inline-block sm:align-middle sm:h-screen", "aria-hidden": "true", children: "\u200B" }), _jsxs("div", { className: `relative z-10 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle ${width} w-full ${className}`, children: [_jsxs("div", { className: "px-4 py-3 border-b border-gray-200 flex justify-between items-center", children: [_jsx("h3", { className: "text-lg font-medium text-interactive-emerald-flash uppercase", id: "modal-title", children: title }), _jsx("button", { type: "button", className: "text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-interactive-emerald-flash", onClick: onClose, "aria-label": "Close", children: _jsx("svg", { className: "h-5 w-5", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) })] }), _jsx("div", { className: "px-4 py-3", children: children })] })] }) }));
};
export default Modal;
//# sourceMappingURL=Modal.js.map